import { create } from 'zustand';

import { WORKSPACE_TABS } from '@ai-studio/shared';

import {
  activatePanel,
  dockState,
  groupOf,
  openPanels,
  parseDock,
  primaryGroup,
  removePanel,
  restorePanel,
  serializeDock,
  split,
  tabs,
  toggleMaximize,
  type DockNode,
  type DockState,
} from '../lib/dock-model.js';

/**
 * The editor window's dock layout: which views and tool panels are where,
 * named layouts the user saved, and the workspace presets (Blender
 * workspaces / Unity layouts). Persisted per user in localStorage, shared by
 * every project tab.
 */

/** Document views: they live in the primary group, switched by the top bar. */
export const DOCUMENT_PANELS: readonly string[] = [...WORKSPACE_TABS.map((t) => t.id), 'settings'];
/** Tool panels that dock around the documents. */
export const TOOL_PANELS: readonly string[] = ['ai', 'history', 'build', 'profiler', 'frameDebugger', 'particles', 'uv', 'graph', 'procgen'];
export const ALL_PANELS: readonly string[] = [...DOCUMENT_PANELS, ...TOOL_PANELS];

const LAYOUT_KEY = 'ai-studio:workspace-layout';
const SAVED_KEY = 'ai-studio:workspace-layouts';
/** Where the pre-docking two-pane split (main | AI) kept its sizes. */
export const LEGACY_DOCK_KEY = 'react-resizable-panels:ai-studio:dock';

export interface WorkspacePreset {
  id: string;
  label: string;
  /** View that gets focus. */
  view: string;
  description: string;
  build: () => DockState;
}

/** Primary group holding every document not placed elsewhere, `active` first shown. */
function primary(active: string, elsewhere: readonly string[] = []): DockNode {
  return tabs('main', DOCUMENT_PANELS.filter((p) => !elsewhere.includes(p)), { active, primary: true });
}

export function factoryLayout(aiSize = 25): DockState {
  return dockState(split('root', 'row', [primary('scene'), tabs('dock-ai', ['ai'])], [100 - aiSize, aiSize]));
}

/** Read the old main|AI split so upgrading keeps the user's dock width. */
export function migrateLegacy(text: string | null): DockState {
  try {
    const old = text ? (JSON.parse(text) as Record<string, unknown>) : null;
    const ai = old && typeof old.ai === 'number' && Number.isFinite(old.ai) ? old.ai : null;
    if (ai !== null && ai < 1) {
      // The dock had been dragged shut.
      return removePanel(factoryLayout(), 'ai');
    }
    return factoryLayout(ai !== null ? Math.min(45, Math.max(15, ai)) : 25);
  } catch {
    return factoryLayout();
  }
}

export const WORKSPACE_PRESETS: readonly WorkspacePreset[] = [
  {
    id: 'default',
    label: 'Default',
    view: 'scene',
    description: 'Views on the left, the AI assistant docked right.',
    build: () => factoryLayout(),
  },
  {
    id: 'modeling',
    label: 'Modeling',
    view: 'model',
    description: 'Model view with the assistant and undo history beside it.',
    build: () =>
      dockState(
        split('root', 'row', [primary('model'), split('side', 'column', [tabs('dock-ai', ['ai']), tabs('dock-history', ['history'])], [60, 40])], [76, 24]),
      ),
  },
  {
    id: 'sculpting',
    label: 'Sculpting',
    view: 'model',
    description: 'A large model viewport with a slim history column.',
    build: () => dockState(split('root', 'row', [primary('model'), tabs('dock-history', ['history'])], [85, 15])),
  },
  {
    id: 'uv',
    label: 'UV Editing',
    view: 'model',
    description: 'Model view beside the UV Editor (Pixel texture editor in the same dock), like Blender’s UV Editing.',
    build: () => dockState(split('root', 'row', [primary('model', ['pixel']), tabs('dock-uv', ['uv', 'pixel'])], [52, 48])),
  },
  {
    id: 'rigging',
    label: 'Rigging',
    view: 'model',
    description: 'Model view (Rig & Animate) with history and the assistant.',
    build: () =>
      dockState(
        split('root', 'row', [primary('model'), split('side', 'column', [tabs('dock-history', ['history']), tabs('dock-ai', ['ai'])], [45, 55])], [74, 26]),
      ),
  },
  {
    id: 'animation',
    label: 'Animation',
    view: 'model',
    description: 'Model and Scene side by side, history along the bottom like a timeline.',
    build: () =>
      dockState(
        split(
          'root',
          'column',
          [split('top', 'row', [primary('model', ['scene']), tabs('dock-scene', ['scene'])], [60, 40]), tabs('dock-history', ['history'])],
          [74, 26],
        ),
      ),
  },
  {
    id: 'lighting',
    label: 'Lighting',
    view: 'scene',
    description: 'Scene editing beside a Play preview and the assistant.',
    build: () =>
      dockState(
        split('root', 'row', [primary('scene', ['play']), split('side', 'column', [tabs('dock-play', ['play']), tabs('dock-ai', ['ai'])], [60, 40])], [58, 42]),
      ),
  },
  {
    id: 'scripting',
    label: 'Scripting',
    view: 'code',
    description: 'Code editor with the Scene view and the assistant alongside.',
    build: () =>
      dockState(
        split('root', 'row', [primary('code', ['scene']), split('side', 'column', [tabs('dock-scene', ['scene']), tabs('dock-ai', ['ai'])], [58, 42])], [56, 44]),
      ),
  },
];

type PresetHook = (presetId: string) => void;
const presetHooks = new Set<PresetHook>();

/**
 * Run code when a workspace preset is applied, e.g. the Model tab entering
 * sculpt mode for "Sculpting" or opening Rig & Animate for "Rigging".
 */
export function onWorkspacePreset(hook: PresetHook): () => void {
  presetHooks.add(hook);
  return () => presetHooks.delete(hook);
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function load(): DockState {
  const store = storage();
  const saved = parseDock(store?.getItem(LAYOUT_KEY) ?? null, ALL_PANELS, DOCUMENT_PANELS);
  if (saved) return saved;
  return migrateLegacy(store?.getItem(LEGACY_DOCK_KEY) ?? null);
}

function loadSaved(): Record<string, string> {
  try {
    const raw = JSON.parse(storage()?.getItem(SAVED_KEY) ?? '{}') as unknown;
    if (!raw || typeof raw !== 'object') return {};
    return Object.fromEntries(Object.entries(raw as Record<string, unknown>).filter((e): e is [string, string] => typeof e[1] === 'string'));
  } catch {
    return {};
  }
}

interface LayoutState {
  dock: DockState;
  /** Preset last applied (cleared by nothing: it's what "Reset to preset" goes back to). */
  preset: string;
  /** User-named layouts, serialized. */
  saved: Record<string, string>;
  set: (dock: DockState) => void;
  applyPreset: (id: string) => string | null;
  save: (name: string) => void;
  load: (name: string) => void;
  remove: (name: string) => void;
  reset: () => void;
  /** Delete every saved layout and go back to the Default workspace (Unity's Revert Factory Settings). */
  revertFactory: () => void;
  /** The current layout as a portable `.layout.json` document. */
  exportLayout: (name: string) => string;
  /** Apply a `.layout.json` document; returns its name, or null when it is not a layout. */
  importLayout: (text: string) => string | null;
  /** Show a panel (reopening it where it was) or hide it. */
  toggle: (panel: string) => void;
  show: (panel: string) => void;
  isOpen: (panel: string) => boolean;
  /** Maximize / restore the group holding `panel`. */
  maximize: (panel: string) => void;
}

function persist(dock: DockState, preset: string): void {
  try {
    storage()?.setItem(LAYOUT_KEY, serializeDock(dock));
    storage()?.setItem(`${LAYOUT_KEY}:preset`, preset);
  } catch {
    // Unavailable storage just means the layout is not remembered.
  }
}

export const useLayout = create<LayoutState>((set, get) => ({
  dock: load(),
  preset: storage()?.getItem(`${LAYOUT_KEY}:preset`) ?? 'default',
  saved: loadSaved(),

  set: (dock) => {
    set({ dock });
    persist(dock, get().preset);
  },

  applyPreset: (id) => {
    const preset = WORKSPACE_PRESETS.find((p) => p.id === id);
    if (!preset) return null;
    const dock = activatePanel(preset.build(), preset.view);
    set({ dock, preset: id });
    persist(dock, id);
    for (const hook of [...presetHooks]) hook(id);
    return preset.view;
  },

  save: (name) => {
    const saved = { ...get().saved, [name]: serializeDock(get().dock) };
    set({ saved });
    try {
      storage()?.setItem(SAVED_KEY, JSON.stringify(saved));
    } catch {
      // ignore
    }
  },

  load: (name) => {
    const dock = parseDock(get().saved[name] ?? null, ALL_PANELS, DOCUMENT_PANELS);
    if (dock) get().set(dock);
  },

  remove: (name) => {
    const saved = { ...get().saved };
    delete saved[name];
    set({ saved });
    try {
      storage()?.setItem(SAVED_KEY, JSON.stringify(saved));
    } catch {
      // ignore
    }
  },

  reset: () => {
    get().applyPreset(get().preset);
  },

  revertFactory: () => {
    set({ saved: {} });
    try {
      storage()?.removeItem(SAVED_KEY);
    } catch {
      // ignore
    }
    get().applyPreset('default');
  },

  exportLayout: (name) => JSON.stringify({ kind: 'ai-studio.layout', version: 1, name, dock: get().dock }, null, 2),

  importLayout: (text) => {
    try {
      const doc = JSON.parse(text) as { kind?: string; name?: string; dock?: unknown };
      if (doc.kind !== 'ai-studio.layout') return null;
      const dock = parseDock(JSON.stringify(doc.dock), ALL_PANELS, DOCUMENT_PANELS);
      if (!dock) return null;
      get().set(dock);
      const name = typeof doc.name === 'string' && doc.name.trim() ? doc.name.trim() : 'Imported layout';
      get().save(name);
      return name;
    } catch {
      return null;
    }
  },

  toggle: (panel) => {
    const { dock } = get();
    get().set(get().isOpen(panel) ? removePanel(dock, panel) : restorePanel(dock, panel));
  },

  show: (panel) => {
    const { dock } = get();
    if (openPanels(dock).includes(panel)) {
      get().set(activatePanel(dock, panel));
      return;
    }
    // Documents reopen as top-bar tabs; tool panels beside the documents.
    const near = primaryGroup(dock)?.id;
    const fallback = DOCUMENT_PANELS.includes(panel)
      ? { zone: 'center' as const, ...(near ? { near } : {}) }
      : { zone: 'right' as const, size: 25 };
    get().set(restorePanel(dock, panel, fallback));
  },

  isOpen: (panel) => openPanels(get().dock).includes(panel),

  maximize: (panel) => {
    const { dock } = get();
    const group = groupOf(dock, panel);
    get().set(toggleMaximize(dock, dock.maximized ? null : (group?.id ?? null)));
  },
}));
